/**
 * The External API usage monitor's documents, as the admin reads them.
 *
 * MIRROR of `apps/b2b-widgets/monitor/src/api-usage/usage.ts`: the monitor writes
 * `external-api/latest.json` and `external-api/daily.json` to the private container
 * `b2bwidget-monitor` on trfrontendstorage, the admin only reads them. Keep the two
 * in step; the monitor is the source of truth.
 */

export type ApiUsagePeriod = "week" | "month";

export interface ApiUsageWindow {
  current: number;
  previous: number;
}

export interface ApiUsageKey {
  key: string;
  /** Last 7 days vs the 7 before. */
  week: ApiUsageWindow;
  /** Last 28 days vs the 28 before. */
  month: ApiUsageWindow;
  lastSeen: string | null;
}

/** A key whose calls fell more than `threshold` under its usual level. */
export interface ApiUsageDrop {
  key: string;
  period: ApiUsagePeriod;
  /** Calls in the window before. */
  from: number;
  /** Calls in the current window. */
  to: number;
  /** The lowest window of the 8 weeks before. Absent in documents written before 2026-11-02. */
  usual?: number;
}

export interface ApiUsageLatest {
  updatedAt: string;
  /** Last whole UTC day counted. */
  through: string;
  /** Every day of the last 56 was scaled to the APIM gateway count. */
  calibrated: boolean;
  threshold: number;
  minWeekly: number;
  /** A drop is also at least this many calls a week under the usual level. Absent before 2026-11-03. */
  minWeeklyLoss?: number;
  /** Recent keys inactive in ExternalApiDB: not judged, not in `keys`. Absent before 2026-11-03. */
  inactive?: string[];
  /** Every key together, as the key `(all keys)`. */
  total: ApiUsageKey;
  keys: ApiUsageKey[];
  drops: ApiUsageDrop[];
  alerted: Record<string, ApiUsagePeriod[]>;
}

/** One key's calls (or every key's, for `(all keys)`) on one UTC day. */
export interface ApiUsageDay {
  day: string;
  requests: number;
  calibrated: boolean;
}

export const API_USAGE_ALL_KEYS = "(all keys)";

/** `0.85` → `-15.0%`; null when there was nothing to compare with. */
export function apiUsageChange(w: ApiUsageWindow): number | null {
  return w.previous > 0 ? (w.current - w.previous) / w.previous : null;
}
